/**
 * Role permissions: every Access Control module, grouped, with View / Create / Edit / Delete per module.
 *
 * Shows and edits the same flags the Access Control page used to (`access_module_config`), in the look of the role
 * dialog: a blue heading per group, checkboxes underneath. A group heading toggles the whole group (and shows
 * "some" when only part of it is granted). The rules live in utils/accessMatrix.js.
 */
import React, { useMemo, useState } from 'react';
import { Checkbox, Input, Spin, Empty } from 'antd';
import modulesTree from '@/components/third-party/modules.json';
import { ACCESS_TYPES, flagState, groupModules, setAccess } from '@/utils/accessMatrix';

const COLUMN = 64;
const rowStyle = { display: 'grid', gridTemplateColumns: `1fr repeat(${ACCESS_TYPES.length}, ${COLUMN}px)`, alignItems: 'center', columnGap: 4 };

const RoleAccessMatrix = ({ rows = [], onChange, disabled = false, loading = false }) => {
  const [search, setSearch] = useState('');
  const groups = useMemo(() => groupModules(rows, modulesTree), [rows]);
  const needle = search.trim().toLowerCase();

  const toggle = (moduleName, key, checked) => onChange?.(setAccess(rows, moduleName, key, checked, modulesTree));

  const boxes = (moduleName) =>
    ACCESS_TYPES.map(({ key, label }) => {
      const { checked, indeterminate } = flagState(rows, moduleName, key, modulesTree);
      return (
        <div key={key} style={{ textAlign: 'center' }}>
          <Checkbox
            aria-label={`${moduleName} ${label}`}
            disabled={disabled}
            checked={checked}
            indeterminate={indeterminate}
            onChange={(e) => toggle(moduleName, key, e.target.checked)}
          />
        </div>
      );
    });

  const visible = groups
    .map((g) => {
      const kids = needle ? g.children.filter((c) => c.module_name.toLowerCase().includes(needle)) : g.children;
      const headMatches = !needle || g.name.toLowerCase().includes(needle);
      return { ...g, kids: headMatches ? g.children : kids };
    })
    .filter((g) => !needle || g.name.toLowerCase().includes(needle) || g.kids.length);

  if (loading) return <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>;
  if (!rows.length) return <Empty description="No modules" />;

  return (
    <div>
      <Input.Search
        allowClear
        placeholder="Search modules…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <div style={{ ...rowStyle, padding: '4px 8px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
        <span>Module</span>
        {ACCESS_TYPES.map(({ key, label }) => <span key={key} style={{ textAlign: 'center' }}>{label}</span>)}
      </div>
      <div style={{ maxHeight: '44vh', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '4px 8px' }}>
        {visible.map((g) => {
          // A group of one module is a single row, not a heading with one line under it.
          const single = g.children.length <= 1;
          const only = single ? (g.children[0] || g.parent) : null;
          if (single) {
            return (
              <div key={g.name} style={{ ...rowStyle, padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
                <span style={{ fontWeight: 600, color: '#1890ff' }}>{only.module_name}</span>
                {boxes(only.module_name)}
              </div>
            );
          }
          return (
            <div key={g.name} style={{ marginBottom: 8 }}>
              <div style={{ ...rowStyle, padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ fontWeight: 600, color: '#1890ff' }}>{g.name}</span>
                {g.parent ? boxes(g.name) : null}
              </div>
              {g.kids.map((c) => (
                <div key={c.module_id} style={{ ...rowStyle, padding: '4px 0 4px 16px' }}>
                  <span>{c.module_name}</span>
                  {boxes(c.module_name)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RoleAccessMatrix;
